import { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiCheckCircle, FiX, FiClock, FiActivity } from "react-icons/fi";
import { GiLotus } from "react-icons/gi";
import { toast } from "react-toastify";
import { userAPI, liveSessionAPI, notificationAPI, practiceVideoAPI } from "../services/api";

const CheckInContext = createContext(null);

const T = {
  orange: "#F29A0C",
  orange2: "#E97818",
  olive: "#6B8A5A",
  olive2: "#8BA47A",
  dark: "#1F1F1F",
  muted: "#999",
  border: "rgba(239,229,216,0.8)"
};

const glass = {
  background: "rgba(255, 253, 250, 0.94)",
  border: "1px solid rgba(242, 154, 12, 0.15)",
  backdropFilter: "blur(40px)",
  WebkitBackdropFilter: "blur(40px)",
  boxShadow: "0 24px 70px rgba(242, 154, 12, 0.12), inset 0 1px 0 #fff"
};

export function CheckInProvider({ children }) {
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkInTime, setCheckInTime] = useState(null);
  const [checkOutTime, setCheckOutTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [restrictedAction, setRestrictedAction] = useState(null);

  // Live Stats States
  const [stats, setStats] = useState({ streakDays: 0, bestStreak: 0, totalPractices: 0, wellnessScore: 0 });
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [videosCount, setVideosCount] = useState(0);
  const [liveSessionsCount, setLiveSessionsCount] = useState(0);
  
  const location = useLocation();
  const token = localStorage.getItem("divyayoga_token");
  const role = localStorage.getItem("divyayoga_role");

  const refreshStats = async () => {
    if (!token || role !== "user") return;
    try {
      // 1. Fetch User Progress (Streak & Total practices from Attendances table)
      const progressRes = await liveSessionAPI.getProgress();
      let streak = 0;
      let bestStreak = 0;
      let totalPractices = 0;
      if (progressRes.data?.success) {
        streak = progressRes.data.streakDays || 0;
        bestStreak = progressRes.data.bestStreak ?? streak;
        totalPractices = progressRes.data.totalPractices !== undefined ? progressRes.data.totalPractices : 0;
      } else if (progressRes.data) {
        streak = progressRes.data.streakDays || 0;
        bestStreak = progressRes.data.bestStreak ?? streak;
        totalPractices = progressRes.data.totalPractices !== undefined ? progressRes.data.totalPractices : 0;
      }

      // Fetch attendance history to calculate wellness score consistency
      let attendanceHistory = [];
      try {
        const attRes = await userAPI.getAttendanceHistory();
        if (attRes.data?.success) {
          attendanceHistory = attRes.data.data || [];
          if (totalPractices === 0 && attendanceHistory.length > 0) {
            totalPractices = attendanceHistory.length;
          }
        }
      } catch (e) {
        console.warn("Failed to fetch attendance history in context:", e);
      }

      // Fetch user's live sessions for active counts
      let completedSessionsCount = 0;
      try {
        const sessionsRes = await liveSessionAPI.getSessions();
        if (sessionsRes.data?.success) {
          const sessionsList = sessionsRes.data.data || [];
          completedSessionsCount = sessionsList.filter(s => s.isCheckedIn).length;
        }
      } catch (e) {
        console.warn("Failed to fetch sessions in context:", e);
      }
      
      // Calculate realistic wellness score based on last 14 days consistency
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const fourteenDaysAgo = new Date();
      fourteenDaysAgo.setDate(today.getDate() - 13);
      fourteenDaysAgo.setHours(0, 0, 0, 0);

      const recentAtts = attendanceHistory.filter(a => {
        const d = new Date(a.attendanceDate);
        return d >= fourteenDaysAgo && d <= today;
      });

      const uniqueRecentDates = new Set(recentAtts.map(a => {
        const d = new Date(a.attendanceDate);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }));
      const consistencyCount = uniqueRecentDates.size; // max 14

      const streakBonus = Math.min(50, streak * 5);
      const consistencyBonus = Math.round((consistencyCount / 14) * 50);
      const score = Math.min(100, streakBonus + consistencyBonus);

      setStats({
        streakDays: streak,
        bestStreak: Math.max(bestStreak, streak),
        totalPractices: totalPractices || attendanceHistory.length || 0,
        wellnessScore: score
      });

      // 2. Fetch Notifications Count
      try {
        const notifRes = await notificationAPI.getNotifications();
        if (notifRes.data?.success && notifRes.data.data) {
          const unread = notifRes.data.data.filter(n => !n.isRead).length;
          setUnreadNotifCount(unread);
        }
      } catch (e) {
        console.warn("Failed to fetch notifications in context:", e);
      }

      // 3. Fetch Practice Videos Count
      try {
        const videosRes = await practiceVideoAPI.getVideos();
        if (videosRes.data?.success && videosRes.data.data) {
          setVideosCount(videosRes.data.data.length);
        }
      } catch (e) {
        console.warn("Failed to fetch videos in context:", e);
      }

      // 4. Fetch Live Sessions Count
      try {
        const sessionsRes = await liveSessionAPI.getSessions();
        if (sessionsRes.data?.success && sessionsRes.data.data) {
          const activeSessions = sessionsRes.data.data.filter(s => {
            const end = new Date(s.scheduledAt);
            end.setMinutes(end.getMinutes() + (s.durationInMinutes || 60));
            return new Date() < end;
          });
          setLiveSessionsCount(activeSessions.length);
        }
      } catch (e) {
        console.warn("Failed to fetch live sessions in context:", e);
      }
    } catch (err) {
      console.warn("refreshStats failed:", err);
    }
  };

  // Fetch status on route change if logged in as user
  useEffect(() => {
    const fetchStatus = async () => {
      if (!token || role !== "user") {
        setLoading(false);
        return;
      }
      try {
        const res = await userAPI.getTodayAttendanceStatus();
        if (res?.data?.success) {
          const data = res.data.data;
          setCheckedIn(data.checkedIn);
          setCheckInTime(data.checkInTime);
          setCheckOutTime(data.checkOutTime);
        }
        await refreshStats();
      } catch (err) {
        console.error("Failed to fetch today check-in status:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [location.pathname, token, role]);

  const getGeoLocationString = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve("N/A");
        return;
      }
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          // Formulate approximate coordinate-based fallback for Karnataka centers
          let fallbackLocation = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
          if (latitude >= 11.5 && latitude <= 14.0 && longitude >= 74.5 && longitude <= 78.5) {
            const city = latitude < 12.6 ? "Mysore" : "Bangalore";
            fallbackLocation = `${city}, Karnataka, India`;
          }

          try {
            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1&email=contact@divyayoga.com`, {
              headers: { 'Accept-Language': 'en' }
            });
            const data = await res.json();
            if (data && data.address) {
              const address = data.address;
              const city = address.city || address.town || address.village || address.suburb || address.county || "";
              const state = address.state || address.state_district || "";
              const country = address.country || "";
              const parts = [city, state, country].filter(Boolean);
              if (parts.length > 0) {
                resolve(parts.join(", "));
                return;
              }
            }
          } catch (e) {
            console.error("Reverse geocoding failed, using fallback:", e);
          }
          resolve(fallbackLocation);
        },
        (error) => {
          console.warn("Geolocation access denied/failed:", error);
          resolve("N/A");
        },
        { enableHighAccuracy: true, timeout: 5000 }
      );
    });
  };

  const checkIn = async () => {
    try {
      setLoading(true);
      toast.info("Acquiring GPS location for secure check-in...");
      const locStr = await getGeoLocationString();
      const res = await userAPI.checkIn(locStr);
      if (res?.data?.success) {
        setCheckedIn(true);
        setCheckInTime(res.data.data.checkInTime);
        setCheckOutTime(null);
        toast.success("Checked in successfully! Have a great practice today. 🙏");
        setShowCheckInModal(false);
        
        await refreshStats();

        // Execute the intercepted action if one exists
        if (restrictedAction) {
          restrictedAction();
          setRestrictedAction(null);
        }
        return true;
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Check-in failed. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
    return false;
  };

  const checkOut = async () => {
    try {
      setLoading(true);
      const res = await userAPI.checkOut();
      if (res?.data?.success) {
        setCheckedIn(false);
        setCheckOutTime(res.data.data.checkOutTime);
        toast.info("Checked out successfully! See you tomorrow. 👋");
        
        await refreshStats();

        return true;
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Check-out failed. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
    return false;
  };

  const triggerCheckInRestriction = (callback) => {
    if (checkedIn) {
      callback();
    } else {
      setRestrictedAction(() => callback);
      setShowCheckInModal(true);
    }
  };

  return (
    <CheckInContext.Provider
      value={{
        checkedIn,
        checkInTime,
        checkOutTime,
        loading,
        checkIn,
        checkOut,
        triggerCheckInRestriction,
        showCheckInModal,
        setShowCheckInModal,
        stats,
        unreadNotifCount,
        videosCount,
        liveSessionsCount,
        refreshStats
      }}
    >
      {children}

      {/* Global Check-in popup modal */}
      <AnimatePresence>
        {showCheckInModal && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setShowCheckInModal(false);
                setRestrictedAction(null);
              }}
              className="absolute inset-0 bg-[#1F1F1F]/40 backdrop-blur-md"
            />

            {/* Modal Box */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 15 }}
              transition={{ type: "spring", damping: 25, stiffness: 220 }}
              className="relative w-full max-w-[460px] rounded-[36px] overflow-hidden p-8 text-center"
              style={glass}
            >
              {/* Close Button */}
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => {
                  setShowCheckInModal(false);
                  setRestrictedAction(null);
                }}
                className="absolute top-5 right-5 w-8 h-8 rounded-full flex items-center justify-center border border-gray-200 bg-white/80 cursor-pointer shadow-sm text-gray-500 hover:text-gray-800"
              >
                <FiX size={15} />
              </motion.button>

              {/* Decorative Brand Icon */}
              <div className="relative mx-auto w-20 h-20 rounded-[24px] flex items-center justify-center mb-6"
                style={{ background: "linear-gradient(135deg, rgba(242,154,12,0.12), rgba(233,120,24,0.12))" }}>
                <GiLotus className="text-[44px]" style={{ color: T.orange }} />
                <motion.div 
                  animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
                  transition={{ duration: 2.5, repeat: Infinity }}
                  className="absolute inset-0 rounded-[24px] border border-[#F29A0C]/25"
                />
              </div>

              {/* Heading */}
              <h2 className="text-[24px] font-black text-gray-900 leading-tight mb-3"
                style={{ fontFamily: "'Poppins', sans-serif" }}>
                Daily Check-In Required ✦
              </h2>

              {/* Description */}
              <p className="text-sm text-gray-500 leading-relaxed max-w-[340px] mx-auto mb-6">
                To access classes, recorded practice videos, diet plans, and slot bookings, please log your check-in for today.
              </p>

              {/* Attendance Card Info */}
              <div className="bg-[#FFF8EE]/80 rounded-[20px] p-4 border border-[#F29A0C]/10 flex items-center justify-around mb-8">
                <div className="flex flex-col items-center">
                  <FiActivity className="text-xl text-[#6B8A5A] mb-1" />
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Status</span>
                  <span className="text-xs font-extrabold text-gray-800 mt-0.5">Not Checked In</span>
                </div>
                <div className="w-[1px] h-10 bg-gray-200" />
                <div className="flex flex-col items-center">
                  <FiClock className="text-xl text-[#E97818] mb-1" />
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Today's Date</span>
                  <span className="text-xs font-extrabold text-gray-800 mt-0.5">
                    {new Date().toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                <motion.button
                  whileHover={{ y: -3, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={checkIn}
                  className="w-full h-[52px] rounded-full text-white font-bold text-sm shadow-[0_12px_30px_rgba(242,154,12,0.3)] cursor-pointer flex items-center justify-center gap-2"
                  style={{ background: "linear-gradient(135deg, #F29A0C, #E97818)" }}
                >
                  <FiCheckCircle size={16} /> Check In Now
                </motion.button>
                <button
                  onClick={() => {
                    setShowCheckInModal(false);
                    setRestrictedAction(null);
                  }}
                  className="text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors py-2 bg-transparent border-none cursor-pointer"
                >
                  Maybe Later
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </CheckInContext.Provider>
  );
}

export function useCheckIn() {
  const context = useContext(CheckInContext);
  if (!context) {
    throw new Error("useCheckIn must be used within a CheckInProvider");
  }
  return context;
}
